import { Link } from "react-router-dom";
import { COLORS } from "../hooks/useAudioRecorder";
import PageShell from "../components/PageShell";
import AuthChip from "../components/AuthChip";
import BackLink from "../components/BackLink";
import AppFooter from "../components/AppFooter";
import TradeLinks from "../components/TradeLinks";
import { JsonLd } from "../hooks/usePageMeta";
import { faqPageLd, webPageLd } from "../seo";
import { TRADES, lineAmount, sampleTotals } from "../data/trades";

/**
 * Public landing page for one trade (/plumbers, /electricians, …).
 *
 * All copy comes from data/trades.js. Layout: headline + intro, a
 * sticky-note "say this" example (same look as the Home TutorialCards), the
 * light-paper sample invoice it produces, a what-to-include checklist, a
 * few trade-specific FAQs (always in the DOM for crawlers), and a CTA back
 * to the record screen.
 */
const MONO = "'JetBrains Mono', ui-monospace, monospace";
const SERIF = "'Instrument Serif', Georgia, serif";
const SANS = "'Inter', system-ui, sans-serif";
const INK_DIM = "#a8a29a";

const money = (n) =>
  "$" + n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function TradePage({ slug }) {
  const trade = TRADES.find((t) => t.slug === slug);
  if (!trade) return null;
  const path = "/" + trade.slug;

  return (
    <PageShell testId={`trade-page-${trade.slug}`} maxWidth={680}>
      <JsonLd data={webPageLd(path)} />
      <JsonLd data={faqPageLd(trade.faqs)} />

      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 18,
        }}
      >
        <BackLink to="/" label="Home" testId="trade-back-link" />
        <AuthChip />
      </header>

      <div
        style={{
          fontFamily: MONO,
          fontSize: 11,
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          color: COLORS.faint,
          marginBottom: 10,
        }}
      >
        // HandInvoice for {trade.name}
      </div>
      <h1
        style={{
          fontFamily: SERIF,
          fontStyle: "italic",
          fontWeight: 400,
          fontSize: 36,
          lineHeight: 1.15,
          margin: "0 0 14px 0",
        }}
      >
        {trade.headline}
      </h1>
      <p style={bodyStyle}>{trade.intro}</p>

      <CtaButton testId="trade-cta-top" />

      <SectionHeading>// say something like</SectionHeading>
      <div
        style={{
          backgroundColor: COLORS.yellow,
          color: COLORS.bg,
          borderRadius: 3,
          padding: "16px 18px",
          fontFamily: MONO,
          fontSize: 13,
          lineHeight: 1.6,
          transform: "rotate(-0.8deg)",
          boxShadow: "0 10px 20px -4px rgba(0,0,0,0.45), 0 3px 6px rgba(0,0,0,0.25)",
          margin: "0 4px",
        }}
      >
        “{trade.say}”
      </div>

      <SectionHeading>// and get an invoice like this</SectionHeading>
      <SampleInvoice trade={trade} />
      <p
        style={{
          fontFamily: MONO,
          fontSize: 11,
          color: COLORS.faint,
          margin: "10px 0 0 0",
          lineHeight: 1.6,
        }}
      >
        // simplified preview. The real PDF also shows your logo, contact
        details, invoice number and due date.
      </p>

      <SectionHeading>
        // what to put on {trade.invoiceNoun}
      </SectionHeading>
      <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
        {trade.include.map((item, i) => (
          <li
            key={i}
            style={{
              ...bodyStyle,
              margin: "0 0 10px 0",
              paddingLeft: 22,
              position: "relative",
            }}
          >
            <span
              aria-hidden
              style={{ position: "absolute", left: 0, color: COLORS.yellow, fontFamily: MONO }}
            >
              ✓
            </span>
            {item}
          </li>
        ))}
      </ul>

      <SectionHeading>// questions</SectionHeading>
      {trade.faqs.map((f, i) => (
        <div
          key={i}
          style={{ borderBottom: `1px dashed ${COLORS.rule}`, padding: "14px 0" }}
        >
          <h3
            style={{
              fontFamily: MONO,
              fontSize: 13,
              fontWeight: 400,
              letterSpacing: "0.04em",
              color: COLORS.text,
              lineHeight: 1.5,
              margin: "0 0 6px 0",
            }}
          >
            {f.q}
          </h3>
          <p style={{ ...bodyStyle, margin: 0 }}>{f.a}</p>
        </div>
      ))}
      <p style={{ ...bodyStyle, marginTop: 14 }}>
        More answers on the{" "}
        <Link to="/faq" style={linkStyle}>
          FAQ page
        </Link>
        .
      </p>

      <CtaButton testId="trade-cta-bottom" />

      <TradeLinks current={trade.slug} />

      <AppFooter />
    </PageShell>
  );
}

const bodyStyle = {
  fontFamily: SANS,
  fontSize: 14,
  lineHeight: 1.7,
  color: INK_DIM,
  margin: "0 0 18px 0",
};

const linkStyle = { color: COLORS.yellow, textDecoration: "none" };

const SectionHeading = ({ children }) => (
  <h2
    style={{
      fontFamily: MONO,
      fontSize: 11,
      fontWeight: 400,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color: COLORS.faint,
      margin: "36px 0 16px 0",
      paddingBottom: 8,
      borderBottom: `1px dashed ${COLORS.rule}`,
    }}
  >
    {children}
  </h2>
);

const CtaButton = ({ testId }) => (
  <Link
    to="/"
    data-testid={testId}
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: 52,
      marginTop: 8,
      borderRadius: 999,
      backgroundColor: COLORS.yellow,
      color: COLORS.bg,
      fontFamily: SANS,
      fontWeight: 800,
      fontSize: 14,
      letterSpacing: "0.12em",
      textTransform: "uppercase",
      textDecoration: "none",
    }}
  >
    Make an invoice now. It's free.
  </Link>
);

/** Light "paper" rendering of the sample — PDFs are light, the app is dark. */
const SampleInvoice = ({ trade }) => {
  const t = sampleTotals(trade);
  const paperInk = "#1a1917";
  const paperFaint = "#7a7468";
  const cell = { padding: "7px 0", borderBottom: "1px solid #e4e0d6" };
  const num = { ...cell, textAlign: "right", fontFamily: MONO, whiteSpace: "nowrap" };
  return (
    <div
      data-testid="trade-sample-invoice"
      style={{
        backgroundColor: "#fbfaf7",
        color: paperInk,
        borderRadius: 4,
        padding: "20px 18px",
        fontFamily: SANS,
        fontSize: 13,
        overflowX: "auto",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 16 }}>
        <div>
          <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: "0.16em", color: paperFaint }}>
            BILL TO
          </div>
          <div style={{ fontWeight: 600, marginTop: 3 }}>{trade.customer}</div>
        </div>
        <div style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: 24 }}>Invoice</div>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 300 }}>
        <thead>
          <tr style={{ fontFamily: MONO, fontSize: 9, letterSpacing: "0.14em", color: paperFaint }}>
            <th style={{ ...cell, textAlign: "left", fontWeight: 400 }}>DESCRIPTION</th>
            <th style={{ ...num, fontWeight: 400, paddingLeft: 10 }}>QTY</th>
            <th style={{ ...num, fontWeight: 400, paddingLeft: 10 }}>RATE</th>
            <th style={{ ...num, fontWeight: 400, paddingLeft: 10 }}>AMOUNT</th>
          </tr>
        </thead>
        <tbody>
          {trade.lines.map((l, i) => (
            <tr key={i}>
              <td style={cell}>{l.description}</td>
              <td style={{ ...num, paddingLeft: 10 }}>
                {l.qty}
                {l.unit ? ` ${l.unit}` : ""}
              </td>
              <td style={{ ...num, paddingLeft: 10 }}>{money(l.rate)}</td>
              <td style={{ ...num, paddingLeft: 10 }}>{money(lineAmount(l))}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginLeft: "auto", maxWidth: 240, marginTop: 12, fontFamily: MONO, fontSize: 12 }}>
        <TotalLine label="SUBTOTAL" value={money(t.gross)} />
        {t.discount > 0 && <TotalLine label="DISCOUNT" value={"−" + money(t.discount)} />}
        {trade.taxPct ? <TotalLine label={`TAX (${trade.taxPct}%)`} value={money(t.tax)} /> : null}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            borderTop: `1.5px solid ${paperInk}`,
            marginTop: 6,
            paddingTop: 8,
          }}
        >
          <span style={{ fontSize: 11, letterSpacing: "0.1em" }}>TOTAL DUE</span>
          <span data-testid="trade-sample-total" style={{ fontSize: 20, fontWeight: 700 }}>
            {money(t.total)}
          </span>
        </div>
      </div>
    </div>
  );
};

const TotalLine = ({ label, value }) => (
  <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", color: "#4a463e" }}>
    <span>{label}</span>
    <span>{value}</span>
  </div>
);
